import { green, red } from '@mui/material/colors';
import dayjs from 'dayjs';

import {
  type GetReportIssuesQuery,
  type GetCompanyEventsQuery,
  type DeliveryAndCollectionItemState,
} from '@/graphql/generated/graphql';

const getMinutesFromTimeString = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

interface OverlapGroup {
  issues: number[];
  size: number;
}

interface OverlapEventGroup {
  events: number[];
  size: number;
}

export const checkOverlap = (issues: GetReportIssuesQuery['getReportIssues']) => {
  const overlapGroups: OverlapGroup[] = [];

  // First pass: identify all overlap groups
  issues.forEach((issue, index) => {
    const overlappingIndices = issues.reduce((acc: number[], otherIssue, otherIndex) => {
      if (index === otherIndex) return acc;
      const issueStart = getMinutesFromTimeString(dayjs(issue.startTime).utc().format('HH:mm') as string);
      const issueEnd = getMinutesFromTimeString(dayjs(issue.endTime).utc().format('HH:mm') as string);
      const otherStart = getMinutesFromTimeString(dayjs(otherIssue.startTime).utc().format('HH:mm') as string);
      const otherEnd = getMinutesFromTimeString(dayjs(otherIssue.endTime).utc().format('HH:mm') as string);

      if ((issueStart <= otherEnd && issueEnd >= otherStart) || (otherStart <= issueEnd && otherEnd >= issueStart)) {
        acc.push(otherIndex);
      }
      return acc;
    }, []);

    if (overlappingIndices.length > 0) {
      // Find or create overlap group
      const foundGroup = overlapGroups.find(
        (group) => group.issues.includes(index) || overlappingIndices.some((i) => group.issues.includes(i)),
      );

      if (foundGroup) {
        foundGroup.issues.push(index, ...overlappingIndices);
        foundGroup.issues = [...new Set(foundGroup.issues)]; // Remove duplicates
        foundGroup.size = foundGroup.issues.length;
      } else {
        overlapGroups.push({
          issues: [index, ...overlappingIndices],
          size: overlappingIndices.length + 1,
        });
      }
    }
  });

  // Second pass: assign columns based on group membership
  return issues.map((issue, index) => {
    const group = overlapGroups.find((g) => g.issues.includes(index));

    if (!group) {
      return {
        ...issue,
        column: 0,
        totalColumns: 1,
      };
    }

    return {
      ...issue,
      column: group.issues.indexOf(index) % group.size,
      totalColumns: group.size,
    };
  });
};

export const checkEventOverlap = (events: GetCompanyEventsQuery['getCompanyEvents'], timeWindowMinutes: number = 5) => {
  const overlapGroups: OverlapEventGroup[] = [];

  events.forEach((event, index) => {
    const eventMinutes = getMinutesFromTimeString(dayjs(event.occurredAt).utc().format('HH:mm') as string);

    const overlappingIndices = events.reduce((acc: number[], otherEvent, otherIndex) => {
      if (index === otherIndex) return acc;
      const otherMinutes = getMinutesFromTimeString(dayjs(otherEvent.occurredAt).utc().format('HH:mm') as string);

      // Check if events are within the time window of each other
      if (Math.abs(eventMinutes - otherMinutes) <= timeWindowMinutes) {
        acc.push(otherIndex);
      }
      return acc;
    }, []);

    if (overlappingIndices.length > 0) {
      const foundGroup = overlapGroups.find(
        (group) => group.events.includes(index) || overlappingIndices.some((i) => group.events.includes(i)),
      );

      if (foundGroup) {
        foundGroup.events.push(index, ...overlappingIndices);
        foundGroup.events = [...new Set(foundGroup.events)];
        foundGroup.size = foundGroup.events.length;
      } else {
        overlapGroups.push({
          events: [index, ...overlappingIndices],
          size: overlappingIndices.length + 1,
        });
      }
    }
  });

  return events.map((event, index) => {
    const group = overlapGroups.find((g) => g.events.includes(index));

    if (!group) {
      return {
        ...event,
        column: 0,
        totalColumns: 1,
      };
    }

    return {
      ...event,
      column: group.events.indexOf(index) % group.size,
      totalColumns: group.size,
    };
  });
};

export const checkItemStatesOverlap = (states: DeliveryAndCollectionItemState[], timeWindowMinutes: number = 5) => {
  const overlapGroups: OverlapEventGroup[] = [];

  if (!states || states.length === 0) return [];

  states.forEach((state, index) => {
    const stateMinutes = getMinutesFromTimeString(dayjs(state.currentStateSetAt).utc().format('HH:mm') as string);

    const overlappingIndices = states.reduce((acc: number[], otherState, otherIndex) => {
      if (index === otherIndex) return acc;
      const otherMinutes = getMinutesFromTimeString(
        dayjs(otherState.currentStateSetAt).utc().format('HH:mm') as string,
      );
      if (Math.abs(stateMinutes - otherMinutes) <= timeWindowMinutes) acc.push(otherIndex);
      return acc;
    }, []);

    if (overlappingIndices.length > 0) {
      const foundGroup = overlapGroups.find(
        (group) => group.events.includes(index) || overlappingIndices.some((i) => group.events.includes(i)),
      );

      if (foundGroup) {
        foundGroup.events.push(index, ...overlappingIndices);
        foundGroup.events = [...new Set(foundGroup.events)];
        foundGroup.size = foundGroup.events.length;
      } else {
        overlapGroups.push({ events: [index, ...overlappingIndices], size: overlappingIndices.length + 1 });
      }
    }
  });

  return states.map((state, index) => {
    const group = overlapGroups.find((g) => g.events.includes(index));
    if (!group) return { ...state, column: 0, totalColumns: 1 };
    return { ...state, column: group.events.indexOf(index) % group.size, totalColumns: group.size };
  });
};

export const getColors = (issueType: string) => {
  switch (issueType.toLowerCase()) {
    case 'external_issue':
      return {
        backgroundColor: red[200],
        backgroundHover: red[100],
        borderLeftColor: red[500],
      };
    case 'internal_issue':
      return {
        backgroundColor: green[200],
        backgroundHover: green[100],
        borderLeftColor: green[500],
      };
    default:
      return {
        backgroundColor: 'rgba(255, 217, 0, 0.3)',
        backgroundHover: 'rgba(255, 217, 0, 0.5)',
        borderLeftColor: 'rgba(255, 217, 0, 0.7)',
      };
  }
};
